<script setup lang="ts">
import { cartMaxPayByBonusesCreate } from "@innoclouds/api";
import { computedAsync } from "@vueuse/core";
import { useTranslation } from "i18next-vue";
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import ArrowLeft from "#/assets/iconsDynamic/ArrowLeft.vue";
import ModalCartRequiredEmail from "#/components/modal/CartRequiredEmail.vue";
import TableOrderPaymentType from "#/components/modal/TableOrderPaymentType.vue";
import OrderBox from "#/components/OrderBox/OrderBox.vue";
import { initPaymentLink } from "#/payment/handlers/basePaymentHandler";
import { useMainStore } from "#/store/main";
import { useModalStore } from "#/store/modals";
import { useModulesStore } from "#/store/modules";
import { useScheduleStore } from "#/store/schedule";
import { useTableStore } from "#/store/table";
import { useThemeStore } from "#/store/theme";
import { useUserStore } from "#/store/user";
import ButtonDefault from "#/ui/buttons/default/ButtonDefault.vue";
import CompletedQRCodeTableTent from "#/ui/cartComplete/completedQRCode/CompletedQRCodeTableTent.vue";
import AnimNum from "#/ui/recountNumber/RecountNumber.vue";
import { formatDeliveryCost } from "#/utils/delivery";
import { formatPrice } from "#/utils/i18next";
import { addNutritions, calculateOrderItemNutritions, emptyNutritions } from "#/utils/nutritions";
import type { GroupedOrderItem } from "./TableOrderPage.types";

const storeMain = useMainStore();
const storeModule = useModulesStore();
const themeStore = useThemeStore();
const tableStore = useTableStore();
const modals = useModalStore();
const userStore = useUserStore();

const { countBonus } = storeToRefs(storeMain);

const router = useRouter();
const route = useRoute();
const { t } = useTranslation();
const schedule = useScheduleStore();

const isButtonDisabled = ref<boolean>(false);
const isBonusActive = ref<boolean>(false);
const isBonusDecimalAllowed = ref<boolean>(false);
const qrLink = ref<string | null>(null);
const isPartialEnabled = computed(() => tableStore?.order?.order_payment_method === "PARTIAL" && !tableStore.isTableTent);
const isPartialPayment = ref<boolean>(false);
const selectedOrderItemIds = ref<string[]>([]);

const disabledStateBtn = computed(() => {
	if (isPartialPayment.value) return selectedOrderItemIds.value.length === 0;
	if (tableStore?.order?.order_payment_method === "DISABLED") return true;
	if (schedule.isClosed || schedule.isNight) return true;
	return isButtonDisabled.value || !tableStore.hasOrder; //|| tableStore.order?.items?.some((item: any) => Boolean(item?.correlation_id))
});

const groupedOrderItems = computed(() => {
	const groups = new Map<GroupedOrderItem["key"], GroupedOrderItem>();
	let guestIndex = 0;

	for (const item of tableStore.order?.items || []) {
		const userId = item?.user?.id;
		const itemCost = Number(item?.total_cost || 0);
		const isPaid = Boolean(item?.is_paid);

		if (userId) {
			const key = `user:${userId}`;
			if (!groups.has(key)) {
				guestIndex += 1;
				const firstName = item?.user?.first_name?.trim();
				const guestTitle = firstName ? `${t(`cart.table.guest`)} ${guestIndex} — ${firstName}` : `${t(`cart.table.guest`)} ${guestIndex}`;
				groups.set(key, { key, title: guestTitle, items: [], total: 0, paid: 0, remaining: 0, hasPaidItems: false });
			}
			const group = groups.get(key)!;
			group.items.push(item);
			group.total += itemCost;
			if (isPaid) {
				group.paid += itemCost;
				group.hasPaidItems = true;
			} else {
				group.remaining += itemCost;
			}
			continue;
		}

		const key = "without-user";
		if (!groups.has(key)) groups.set(key, { key, title: t(`cart.table.guest.without`), items: [], total: 0, paid: 0, remaining: 0, hasPaidItems: false });
		const group = groups.get(key)!;
		group.items.push(item);
		group.total += itemCost;
		if (isPaid) {
			group.paid += itemCost;
			group.hasPaidItems = true;
		} else {
			group.remaining += itemCost;
		}
	}

	return Array.from(groups.values());
});

const selectedOrderItems = computed(() => {
	const selectedIds = new Set(selectedOrderItemIds.value);
	return (tableStore.order?.items || []).filter((item) => selectedIds.has(item.id as string) && !item?.is_paid);
});

const unpaidItemIds = computed(() => (tableStore.order?.items || []).filter((item) => !item?.is_paid).map((item) => item.id));

const isAllUnpaidItemsSelected = computed(() => {
	if (!unpaidItemIds.value.length) return false;
	const selectedSet = new Set(selectedOrderItemIds.value);
	return unpaidItemIds.value.every((id) => selectedSet.has(id as string));
});

const selectedItemsTotal = computed(() => {
	return selectedOrderItems.value.reduce((total, item) => total + Number(item?.total_cost || 0), 0);
});

const unpaidItemsTotal = computed(() => {
	return (tableStore.order?.items || []).reduce((total, item) => {
		if (item?.is_paid) return total;
		return total + Number(item?.total_cost || 0);
	}, 0);
});

const remainingAmountDelta = computed(() => +(Number(tableStore.order?.remaining_amount || 0) - unpaidItemsTotal.value).toFixed(2));
const hasRemainingAmountDelta = computed(() => Math.abs(remainingAmountDelta.value) > 0);
const shouldShowPartialDelta = computed(() => isPartialPayment.value && isAllUnpaidItemsSelected.value && hasRemainingAmountDelta.value);
const partialPaymentTotal = computed(() => {
	const total = selectedItemsTotal.value - countBonus.value;
	if (!shouldShowPartialDelta.value) return total;
	return total + remainingAmountDelta.value;
});

const deliveryCost = computed(() => formatDeliveryCost(tableStore.data?.branch?.delivery_cost));

const totalNutritions = computed(() => {
	let total = emptyNutritions();
	for (const item of tableStore.order?.items ?? []) {
		total = addNutritions(total, calculateOrderItemNutritions(item));
	}
	return total;
});

const maxBonuses = computedAsync(
	async (onCancel) => {
		const abortController = new AbortController();
		onCancel(() => abortController.abort());

		if (!isBonusActive.value || !userStore.data?.balance) return 0;

		const response = await cartMaxPayByBonusesCreate({ order_id: tableStore.order?.id as string }, { signal: abortController.signal });
		if (!response?.by_bonuses) return 0;
		if (userStore.isAuthorized && "balance" in response && response.balance !== userStore.data?.balance) await userStore.invalidate();

		isBonusDecimalAllowed.value = response.non_integers_allowed || false;

		return Math.min(tableStore.order!.remaining_amount, userStore.data?.balance, response.by_bonuses);
	},
	0,
	{ shallow: true },
);

const goBack = () => router.push(`/${route.params.orgId}/`);

const applyEmail = (_orderId: string, _providerType: string, email?: string) => {
	return makePayment(email);
};

const createrOrder = async () => {
	if (isPartialEnabled.value && !isPartialPayment.value) return modals.open("table-order-payment-type");
	if (isPartialEnabled.value && selectedOrderItemIds.value.length === 0) return storeModule.showNotification(t(`cart.footer.partial-payment.notice`));

	return makePayment();
};

const makePayment = async (email?: string) => {
	if (!email && !userStore.data?.email?.length) return modals.open("cart-required-email", { orderId: tableStore.order!.id, providerType: "YOOKASSA" });

	isButtonDisabled.value = true;

	try {
		const extra: Record<string, unknown> = {
			items: tableStore.order?.items?.map((item) => item.id) || [],
		};
		if (tableStore.isTableTent && themeStore.data?.qr_use_qr_for_payment) extra["disable_back_btn"] = true;
		if (isPartialPayment.value) extra["items"] = selectedOrderItemIds.value;
		if (tableStore.order?.remaining_amount !== tableStore.order?.total_cost) extra["amount"] = tableStore.order?.remaining_amount;
		if (isPartialPayment.value) extra["amount"] = selectedItemsTotal.value;
		if (extra["amount"]) extra["amount"] = +(+extra["amount"]).toFixed(2);
		if (email) extra["email"] = email;
		if (deliveryCost.value !== null) extra["delivery_cost"] = deliveryCost.value;

		const response = await initPaymentLink(tableStore.order!.id, "YOOKASSA", extra);
		if (response.url) {
			if (tableStore.isTableTent && themeStore.data?.qr_use_qr_for_payment) qrLink.value = response.url;
			else window.location.replace(response.url);
		}
	} catch (error: any) {
		storeModule.showNotification(error?.response?.data?.user_friendly_message || t(`cart.notification.order.payment-error`));
		throw error;
	} finally {
		isButtonDisabled.value = false;
	}
};

// @TODO: Для кнопки списания бонусов, поскольку скрыли не используется, но в будущем будет использоваться
const toggleBonuses = () => {
	if (userStore.data?.balance && userStore.data.balance > 0) {
		isBonusActive.value = !isBonusActive.value;
	} else {
		storeModule.showNotification(t(`cart.notification.bonus.empty`));
	}
};

const preventNonNumeric = (e: { key: string; preventDefault: () => void }) => {
	const excludeSymbols = isBonusDecimalAllowed.value ? ["a", "A", "+", "-", "."] : ["a", "A", "+", "-", ".", ","];
	if (excludeSymbols.includes(e.key)) e.preventDefault();
};

const validateInput = () => {
	let newCount: number = isBonusDecimalAllowed.value ? +countBonus.value.toFixed(2) : Math.floor(countBonus.value) || 0;
	if (newCount > maxBonuses.value) newCount = maxBonuses.value;
	if (newCount !== countBonus.value) storeMain.setBonus(newCount);
};

const toggleOrderItemSelection = (itemId: string, isSelected: boolean) => {
	if (!isPartialPayment.value) return;
	const orderItem = (tableStore.order?.items || []).find((item) => item.id === itemId);
	if (orderItem?.is_paid) return;

	if (isSelected) {
		if (!selectedOrderItemIds.value.includes(itemId)) selectedOrderItemIds.value = [...selectedOrderItemIds.value, itemId];
		return;
	}

	selectedOrderItemIds.value = selectedOrderItemIds.value.filter((id) => id !== itemId);
};

const toggleGroupSelection = (group: { items: any[] }) => {
	if (!isPartialPayment.value) return;

	const unpaidIds = group.items.filter((item) => !item?.is_paid).map((item) => item.id);
	if (unpaidIds.length === 0) return;

	const selectedSet = new Set(selectedOrderItemIds.value);
	const isAllGroupUnpaidSelected = unpaidIds.every((id) => selectedSet.has(id));

	if (isAllGroupUnpaidSelected) {
		selectedOrderItemIds.value = selectedOrderItemIds.value.filter((id) => !unpaidIds.includes(id));
		return;
	}

	const nextSelected = new Set(selectedOrderItemIds.value);
	unpaidIds.forEach((id) => {
		nextSelected.add(id);
	});
	selectedOrderItemIds.value = Array.from(nextSelected);
};

const selectPaymentType = (type: string) => {
	if (type === "full") {
		isPartialPayment.value = false;
		return makePayment();
	}
	if (type === "partial") {
		isPartialPayment.value = true;
		return;
	}
};

watch(isPartialPayment, (enabled) => {
	if (!enabled) selectedOrderItemIds.value = [];
});

watch(
	() => tableStore.order?.items,
	(items) => {
		if (!items?.length) {
			selectedOrderItemIds.value = [];
			return;
		}

		const actualIds = new Set(items.filter((item) => !item?.is_paid).map((item) => item.id));
		selectedOrderItemIds.value = selectedOrderItemIds.value.filter((id) => actualIds.has(id));
	},
	{ deep: true },
);

watch(
	() => [route.query.pay === "1", tableStore.isTableLoaded, tableStore.order?.id, tableStore.selectedOrder?.id, disabledStateBtn.value] as const,
	([isAutoPayRequested, isTableLoaded, loadedOrderId, selectedOrderId, isDisabled]) => {
		if (!isAutoPayRequested || !isTableLoaded || !loadedOrderId || loadedOrderId !== selectedOrderId || isDisabled) return;
		router.replace({ name: "order-ts", params: route.params });
		createrOrder().catch(() => {});
	},
	{ immediate: true },
);
</script>

<template>
	<div class="table_order_wrapper">
		<ModalCartRequiredEmail @confirm="applyEmail" />
		<TableOrderPaymentType @select="selectPaymentType" />
		<CompletedQRCodeTableTent
			v-if="tableStore.isTableTent && qrLink"
			:price="(tableStore.order?.remaining_amount ?? 0) - countBonus"
			:link="qrLink"
			@cancel="qrLink = null"
		/>
		<header class="table_order_header_content">
			<div class="table_order_nutritions_qr_wrapper">
				<div class="table_order_qr_button_wrapper">
					<div class="arrow_wrapper" @click="goBack">
						<ArrowLeft class="table_order_header_back_button" />
					</div>
				</div>
				<div v-if="themeStore.data?.show_nutrition_in_cart" class="table_order_header" :style="{ paddingRight: '10px' }">
					<div class="table_order_header_info">
						<p>
							<AnimNum :price="totalNutritions.energy_amount" :duration="800" />
						</p>
						<p class="table_order_info_name">{{ t(`card.nutritional.calories`) }}</p>
					</div>
					<div class="table_order_header_info">
						<p>
							<AnimNum :price="totalNutritions.proteins_amount" :duration="900" />
							{{ t(`cart.header.nutritional.symbol`) }}
						</p>
						<p class="table_order_info_name">{{ t(`card.nutritional.proteins`) }}</p>
					</div>
					<div class="table_order_header_info">
						<p>
							<AnimNum :price="totalNutritions.fat_amount" :duration="1000" />
							{{ t(`cart.header.nutritional.symbol`) }}
						</p>
						<p class="table_order_info_name">{{ t(`card.nutritional.fats`) }}</p>
					</div>
					<div class="table_order_header_info">
						<p>
							<AnimNum :price="totalNutritions.carbohydrates_amount" :duration="1100" />
							{{ t(`cart.header.nutritional.symbol`) }}
						</p>
						<p class="table_order_info_name">{{ t(`card.nutritional.carbohydrates`) }}</p>
					</div>
				</div>
			</div>
		</header>
		<div v-if="tableStore.order?.id" class="table_order_table_wrapper">
			<p class="table_order_desk_number">{{ t(`cart.table.desk-number`, { number: tableStore.data?.number }) }}</p>
			<p class="table_order_indent_number">{{ t(`cart.table.order-number`, { number: tableStore.order.plugin_order?.number ?? '-' }) }}</p>
		</div>

		<div class="table_order_cards" :class="{ selectable: isPartialPayment }">
			<template v-if="tableStore.order?.items?.length">
				<template v-if="groupedOrderItems.length === 1">
					<OrderBox
						v-for="item in tableStore.order.items"
						:key="item.idSystem"
						:cardItem="item"
						:selectable="isPartialPayment"
						:selected="selectedOrderItemIds.includes(item.id!)"
						@update:selected="(value) => toggleOrderItemSelection(item.id!, value)"
					/>
				</template>
				<template v-else>
					<section v-for="group in groupedOrderItems" :key="group.key" class="table_order_guest_group">
						<p class="table_order_guest_title" @click="toggleGroupSelection(group)">{{ group.title }}</p>
						<OrderBox
							v-for="item in group.items"
							:key="item.idSystem"
							:cardItem="item"
							:selectable="isPartialPayment"
							:selected="selectedOrderItemIds.includes(item.id)"
							@update:selected="(value) => toggleOrderItemSelection(item.id, value)"
						/>
						<div class="table_order_guest_group_total">
							<div class="row">
								<span>{{ t(`cart.footer.total.title`) }}</span>
								<span @click="toggleGroupSelection(group)">
									<AnimNum :price="group.total" :symbol="storeMain.config_lib.currency_symbol" />
								</span>
							</div>
						</div>
					</section>
				</template>
			</template>
		</div>
		<div class="table_order_footer_wrapper">
			<p v-if="isBonusActive && maxBonuses > 0" class="footer_title">
				{{ t(`cart.footer.bonus.withdraw.prefix`) }}
				<input
					type="number"
					placeholder="0"
					:style="{ maxWidth: `${countBonus.toString().length || 1}em` }"
					:disabled="!userStore.isAuthorized || userStore.data?.balance === 0"
					v-model.number="countBonus"
					@input="validateInput"
					min="0"
					:max="maxBonuses"
					step=".01"
					@keypress="preventNonNumeric"
				>
				{{ t(`cart.footer.bonus.withdraw.postfix`, { count: countBonus }) }}
			</p>
			<p v-if="isBonusActive && maxBonuses > 0" class="footer_sales">
				{{ t(`cart.footer.bonus.balance`, { count: formatPrice(maxBonuses), balance: formatPrice(userStore.data?.balance || 0) }) }}
			</p>
			<p v-else-if="isBonusActive">{{ t(`cart.footer.bonus.balance.empty`) }}</p>
			<input
				v-else-if="isBonusActive"
				type="range"
				class="footer_range"
				v-model.number="countBonus"
				:step="isBonusDecimalAllowed ? '.01' : '1'"
				:min="0"
				:max="maxBonuses"
			>

			<ul class="list_price">
				<li v-if="tableStore.order && (tableStore.order.total_cost ?? 0) - (tableStore.order.remaining_amount ?? 0) > 0">
					<p>{{ t(`cart.footer.amount.title`) }}</p>
					<p>
						<AnimNum :price="tableStore.order?.total_cost ?? 0" :symbol="storeMain.config_lib.currency_symbol" />
					</p>
				</li>
				<li v-if="tableStore.order && (tableStore.order.total_cost ?? 0) - (tableStore.order.remaining_amount ?? 0) > 0">
					<p>{{ t(`cart.footer.paid.title`) }}</p>
					<p>
						<AnimNum :price="(tableStore.order.total_cost ?? 0) - (tableStore.order.remaining_amount ?? 0)" :symbol="storeMain.config_lib.currency_symbol" />
					</p>
				</li>
				<li v-if="tableStore.order?.remaining_amount && isPartialPayment">
					<p>{{ t(`cart.footer.remaining-to-pay.title`) }}</p>
					<p>
						<AnimNum :price="tableStore.order.remaining_amount ?? 0" :symbol="storeMain.config_lib.currency_symbol" />
					</p>
				</li>
				<li v-if="shouldShowPartialDelta">
					<p>{{ remainingAmountDelta > 0 ? t(`cart.footer.supplement.title`) : t(`cart.footer.discount.title`) }}</p>
					<p>
						<AnimNum :price="Math.abs(remainingAmountDelta)" :symbol="storeMain.config_lib.currency_symbol" />
					</p>
				</li>
				<li v-if="isBonusActive">
					<p>{{ t(`cart.footer.bonus.title`) }}</p>
					<p>
						<AnimNum :price="countBonus" :symbol="t(`cart.footer.bonus.sign`)" />
					</p>
				</li>
				<li v-if="deliveryCost !== null">
					<p class="accept_field">{{ t(`cart.footer.delivery-cost.title`) }}</p>
					<p class="accept_field">
						<AnimNum :price="deliveryCost" :symbol="storeMain.config_lib.currency_symbol" />
					</p>
				</li>
				<li v-if="tableStore.order?.remaining_amount && !isPartialPayment">
					<p class="accept_field">{{ t(`cart.footer.final.title`) }}</p>
					<p class="accept_field">
						<AnimNum :price="tableStore.order.remaining_amount - countBonus" :symbol="storeMain.config_lib.currency_symbol" />
					</p>
				</li>
				<li v-if="isPartialPayment">
					<p class="accept_field">{{ t(`cart.footer.final.title`) }}</p>
					<p class="accept_field">
						<AnimNum :price="partialPaymentTotal" :symbol="storeMain.config_lib.currency_symbol" />
					</p>
				</li>
			</ul>
			<div v-if="schedule.isClosed" class="table_order_schedule_notice">{{ schedule.disclaimerText }}</div>
			<div v-if="tableStore.order?.remaining_amount && themeStore.data?.qr_show_pay_button_ts !== false" class="wrapper_footer_table_order_btn">
				<ButtonDefault
					v-if="isPartialPayment"
					:textButtonDefault="t(`cart.button.cancel`)"
					:onClick="() => (isPartialPayment = false)"
					class="button_cancel_partial_payment"
				/>
				<ButtonDefault
					v-if="tableStore?.order?.order_payment_method !== 'DISABLED'"
					:textButtonDefault="t(`cart.button.pay-order`)"
					:onClick="createrOrder"
					class="button_payment_cart"
					:disabled="disabledStateBtn"
				/>
			</div>
			<div v-if="isPartialPayment && selectedOrderItemIds.length === 0" class="table_order_footer_text">{{ t(`cart.footer.partial-payment.notice`) }}</div>
		</div>
	</div>
</template>

<style scoped lang="scss">
  @use './tableOrderPage.scss';
</style>
